import axios from 'axios';
import { useCallback, useContext, useEffect, useMemo } from 'react';
import { FbPixelContext } from '../../contexts/FbPixelContext';
import { ApplicationContext } from '../../contexts/ApplicationContext';
import {
	trackInitiateCheckoutOnPixel,
	trackPurchaseOnPixel
} from './pixelEvents';

/**
 * Pixel da PLATAFORMA (/integrations/fbpixel). Cada evento vai só para ele
 * (`trackSingle`) — ver `pixelEvents.ts`.
 *
 * As funções saem do pixel guardado no contexto, então funcionam também na
 * tela montada depois que outra já buscou o pixel (antes ficavam mudas ali).
 * Sem pixel, não fazem nada.
 */
export const useFbPixel = () => {
	const { fbPixel, setFbPixel } = useContext(FbPixelContext);
	const { customization } = useContext(ApplicationContext);

	useEffect(() => {
		if (fbPixel) return;
		if (!customization?.url) return;

		axios
			.get<{ pixel: string }>(
				`${customization?.url || ''}/integrations/fbpixel`
			)
			.then((response) => {
				if (!response.data?.pixel) return;

				setFbPixel(response.data.pixel);
			})
			.catch(() => {
				throw new Error('Error while fetch FB Pixel.');
			});
	}, [customization]);

	const trackInitiateCheckout = useCallback(
		(data?: unknown) => {
			if (fbPixel) trackInitiateCheckoutOnPixel(fbPixel, data);
		},
		[fbPixel]
	);

	/** Só para compra paga; `reference` vira o eventID de deduplicação. */
	const trackPurchase = useCallback(
		(input: { value: number; reference?: string | null }) => {
			if (fbPixel) trackPurchaseOnPixel(fbPixel, input);
		},
		[fbPixel]
	);

	return useMemo(
		() => ({
			pixelId: fbPixel,
			trackInitiateCheckout,
			trackPurchase
		}),
		[fbPixel, trackInitiateCheckout, trackPurchase]
	);
};
